import React, {useEffect, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {FormikProvider} from 'formik';
import moment from 'moment';
import {useCustomerDetailsUIContext} from '../CustomerDetailsUIContext';
import {useTextSuggestionsContext} from '../../../_common/contexts/TextSuggestionsContext';
import StyledFieldEmailChips from '../../../_common/components/StyledFieldEmailChips/StyledFieldEmailChips';
import {StyledFieldInput} from '../../../_common/components/FieldInput';
import {StyledFieldTextareaAutosize} from '../../../_common/components/FieldTextareaAutosize';
import GridEditableRows from '../GridEditableRows';
import Tabs from '../../../_common/components/Tabs';
import PopupPreviewDocument from '../PopupPreviewDocument';
import ButtonTextSuggestions from '../../../_common/components/ButtonTextSuggestions';
import Spinner from '../../../_common/components/Spinner';
import {StyledFieldDatepicker} from '../../../_common/components/FieldDatepicker';
import FieldSelect from '../../../_common/components/FieldSelect';
import ModalContainer from '../../../_common/components/ModalContainer';
import ModalHeader from '../../../_common/components/ModalHeader';
import ModalContent from '../../../_common/components/ModalContent';
import ModalButtonsContainer from '../../../_common/components/ModalButtonsContainer';
import ButtonAction from '../../../_common/components/ButtonAction';
import Unit from '../../../_common/components/Unit';
import {optionsDiscountType} from '../../constants/options';
import {documentTypes as T} from '../../constants/documentTypes';
import {gridTypes as G} from '../../constants/gridTypes';
import {buttonCancel, buttonSubmit, largeButtonsPadding} from '../../../_common/styles/styles';
import {divideThousands, floatValue} from '../../../../../utils/format';
import {getTotalCommonDocsAmounts, getTotalPaid} from '../../utils/countTotalAmounts';
import {filterOptions} from '../../../_common/utils/options';

const PopupCreateDocument = ({
                               buttonVariant = '',
                               icon = '',
                               iconButtonType,
                               buttonIcon,
                               documentType,
                               documentId = '',
                               withCopiedData = false,
                               title = ''
                             }) => {
  const [show, setShow] = useState(false);
  const [showApprove, setShowApprove] = useState(false);
  const handleCloseModal = () => {
    resetForm();
    setShow(false);
  };
  const handleShowModal = (documentId) => {
    if (withCopiedData) {
      getCopiedDocumentData(documentId);
    }
    setShow(true);
  };
  const prefix = 'customer-details-popup-create-document';

  const {
    generalDetails,
    handleCreateDocument,
    formikCreateDocument,
    handlePreviewDocument,
    previewDocumentLink,
    getCopiedDocumentData,
    getCopiedDocumentDataCondition,
    copiedDocumentData,
    documentsData,
    setCreateFromCopiedDataMark,
    loadingCopiedDocumentData
  } = useCustomerDetailsUIContext();

  const {textSuggestions} = useTextSuggestionsContext();
  const {description, remarks, footer} = textSuggestions;
  const getSuggestions = array => Array.isArray(array) ? array.filter(item => item.documentTypeID === documentType) : [];

  const {values, setFieldValue, handleChange, resetForm, isValid, dirty, validateForm} = formikCreateDocument;
  const {email} = generalDetails;
  const getCopiedEmails = () => {
    const filteredInitDocData = documentsData.filter(item => item.id === documentId);

    return filteredInitDocData[0].client.emails;
  };
  const initialEmailsValue = withCopiedData
    ? getCopiedEmails()
    : [email];
  const createdFromDocType = copiedDocumentData.type;
  const linkedDocumentsIds = copiedDocumentData.id;
  const publicDocId = copiedDocumentData.number;

  useEffect(() => {
    if (getCopiedDocumentDataCondition()) {
      setTimeout(() => {
        validateForm();
      }, 1000);
    }
  }, [values]);

  const disabledButtonCondition = getCopiedDocumentDataCondition()
    ? !isValid
    : !(isValid && dirty);

  const amountSection = (type = 'horizontal') => {
    const commonProps = {
      customInputStyles: 'text-left',
      customContainerStyles: 'mx-2 w-100',
      onChange: handleChange,
    };

    const containerStyles = type === 'horizontal'
      ? 'd-flex justify-content-between w-50'
      : 'w-50';

    return (
      <div className={containerStyles}>
        <Unit
          text={<FormattedMessage id={`${prefix}-label-totalDiscount-header`} defaultMessage="Total discount"/>}
          icon={'/media/common/Discount_red.svg'}
          customClassnames={`w-100 ${type === 'horizontal' ? 'mr-2' : 'mb-4'}`}
        >
          <div className="d-flex justify-content-between align-items-center">
            <StyledFieldInput
              fieldId={'amount'}
              label={<FormattedMessage id={`${prefix}-label-amount`} defaultMessage="amount"/>}
              onChange={floatValue}
              {...commonProps}
            />
            <FormikProvider value={formikCreateDocument}>
              <FieldSelect
                fieldId={'type'}
                value={filterOptions(values.type)(optionsDiscountType)}
                options={optionsDiscountType}
                label={<FormattedMessage id={`${prefix}-label-type`} defaultMessage="Type"/>}
                onChange={value => setFieldValue('type', value)}
                {...commonProps}
              />
            </FormikProvider>
            <StyledFieldInput
              dir={'ltr'}
              fieldId={'totalDiscount'}
              label={<FormattedMessage id={`${prefix}-label-totalDiscount`} defaultMessage="totalDiscount"/>}
              value={divideThousands(getTotalCommonDocsAmounts(values, getCopiedDocumentDataCondition()).totalDiscount)}
              disabled={true}
              {...commonProps}
            />
          </div>
        </Unit>
        <Unit
          text={<FormattedMessage id={`${prefix}-label-total-header`} defaultMessage="Total"/>}
          icon={'/media/common/Money_navy.svg'}
          customClassnames={`w-100 ${type === 'horizontal' ? 'ml-2' : ''}`}
        >
          <div className={'d-flex justify-content-between align-items-center'}>
            <StyledFieldInput
              dir={'ltr'}
              fieldId={'totalWithoutVAT'}
              label={<FormattedMessage id={`${prefix}-label-totalWithoutVAT`} defaultMessage="Total without VAT"/>}
              value={divideThousands(getTotalCommonDocsAmounts(values, getCopiedDocumentDataCondition()).totalWithoutVAT)}
              disabled={true}
              {...commonProps}
            />
            <StyledFieldInput
              dir={'ltr'}
              fieldId={'totalVAT'}
              label={<FormattedMessage id={`${prefix}-label-totalVAT`} defaultMessage="totalVAT"/>}
              value={divideThousands(getTotalCommonDocsAmounts(values, getCopiedDocumentDataCondition()).totalVAT)}
              disabled={true}
              {...commonProps}
            />
            <StyledFieldInput
              dir={'ltr'}
              fieldId={'total'}
              label={<FormattedMessage id={`${prefix}-label-total`} defaultMessage="total"/>}
              value={divideThousands(getTotalCommonDocsAmounts(values, getCopiedDocumentDataCondition()).total)}
              disabled={true}
              {...commonProps}
            />
          </div>
        </Unit>
      </div>
    );
  };

  const renderGridsAmounts = (type) => {
    switch (type) {
      case T.PRICE_QUOTE:
      case T.WORK_ORDER:
      case T.INVOICE:
      case T.TRANSACTION_ACCOUNT:
        return (
          <div className={'d-flex justify-content-between mb-4'}>
            <Unit
              text={<FormattedMessage id={`${prefix}-label-list-header`} defaultMessage="List"/>}
              icon={'/media/common/List_primary.svg'}
              customClassnames={'mr-4 w-50'}
            >
              <GridEditableRows gridType={G.COMMON_DOCS}/>
            </Unit>
            {amountSection('horizontal')}
          </div>
        );
      case T.RECEIPT:
        return (
          <div className={'d-flex justify-content-between mb-4'}>
            <Unit
              text={<FormattedMessage id={`${prefix}-label-list-header`} defaultMessage="List"/>}
              icon={'/media/common/List_primary.svg'}
              customClassnames={'w-100 mr-2'}
            >
              <Tabs
                containerClassnames={'bg-transparent border-0 position-absolute top-0 shadow-none pl-40'}
                items={[
                  {
                    key: 'cheque',
                    title: 'צ\'ק',
                    icon: null,
                    content: <GridEditableRows gridType={G.CHEQUE}/>,
                  },
                  {
                    key: 'bankTransfer',
                    title: 'העברה בנקאית',
                    icon: null,
                    content: <GridEditableRows gridType={G.BANK_TRANSFER}/>,
                  },
                  {
                    key: 'creditCard',
                    title: 'אשראי',
                    icon: null,
                    content: <GridEditableRows gridType={G.CREDIT_CARD}/>,
                  },
                  {
                    key: 'paymentApp',
                    title: 'אפליקציית תשלום',
                    icon: null,
                    content: <GridEditableRows gridType={G.PAYMENT_APP}/>,
                  },
                  {
                    key: 'payPal',
                    title: 'פייפאל',
                    icon: null,
                    content: <GridEditableRows gridType={G.PAY_PAL}/>,
                  },
                  {
                    key: 'cash',
                    title: 'מזומן',
                    icon: null,
                    content: <GridEditableRows gridType={G.CASH}/>,
                  },
                  {
                    key: 'other',
                    title: 'אחר',
                    icon: null,
                    content: <GridEditableRows gridType={G.OTHER}/>,
                  },
                  {
                    key: 'withholdingTax',
                    title: 'ניכוי במקור',
                    icon: null,
                    content: <GridEditableRows gridType={G.WITHHOLDING_TAX}/>,
                  },
                ]}
              />
            </Unit>
            <Unit
              text={<FormattedMessage id={`${prefix}-label-total-receipts`} defaultMessage="Total"/>}
              icon={'/media/common/Money_navy.svg'}
              customClassnames={'w-250px ml-2'}
            >
              <StyledFieldInput
                dir={'ltr'}
                fieldId={'totalPaid'}
                label={<FormattedMessage id={`${prefix}-label-total-receipts`} defaultMessage="total"/>}
                customContainerStyles={'w-200px'}
                customInputStyles={'text-left bg-secondary'}
                value={divideThousands(getTotalPaid(values)) || '0.00'}
                disabled={true}
              />
            </Unit>
          </div>
        );
      case T.INVOICE_RECEIPT:
      default:
        return (
          <>
            <div className={'d-flex justify-content-between mb-4'}>
              <Unit
                text={<FormattedMessage id={`${prefix}-subheader-services-details`} defaultMessage={'Services'}/>}
                icon={'/media/common/List_primary.svg'}
                customClassnames={'mr-4 w-75'}
              >
                <GridEditableRows gridType={G.COMMON_DOCS}/>
              </Unit>
              {amountSection('vertical')}
            </div>
            <div className={'d-flex justify-content-between mb-4'}>
              <Unit
                text={<FormattedMessage id={`${prefix}-subheader-receipts-details`} defaultMessage={'Receipts'}/>}
                icon={'/media/common/List_primary.svg'}
                customClassnames={'w-100 mr-2'}
              >
                <Tabs
                  containerClassnames={'bg-transparent border-0 position-absolute top-0 shadow-none pl-40 ml-10'}
                  items={[
                    {
                      key: 'cheque',
                      title: 'צ\'ק',
                      icon: null,
                      content: <GridEditableRows gridType={G.CHEQUE}/>,
                    },
                    {
                      key: 'bankTransfer',
                      title: 'העברה בנקאית',
                      icon: null,
                      content: <GridEditableRows gridType={G.BANK_TRANSFER}/>,
                    },
                    {
                      key: 'creditCard',
                      title: 'אשראי',
                      icon: null,
                      content: <GridEditableRows gridType={G.CREDIT_CARD}/>,
                    },
                    {
                      key: 'paymentApp',
                      title: 'אפליקציית תשלום',
                      icon: null,
                      content: <GridEditableRows gridType={G.PAYMENT_APP}/>,
                    },
                    {
                      key: 'payPal',
                      title: 'פייפאל',
                      icon: null,
                      content: <GridEditableRows gridType={G.PAY_PAL}/>,
                    },
                    {
                      key: 'cash',
                      title: 'מזומן',
                      icon: null,
                      content: <GridEditableRows gridType={G.CASH}/>,
                    },
                    {
                      key: 'other',
                      title: 'אחר',
                      icon: null,
                      content: <GridEditableRows gridType={G.OTHER}/>,
                    },
                    {
                      key: 'withholdingTax',
                      title: 'ניכוי במקור',
                      icon: null,
                      content: <GridEditableRows gridType={G.WITHHOLDING_TAX}/>,
                    },
                  ]}
                />
              </Unit>
              <Unit
                text={<FormattedMessage id={`${prefix}-label-total-receipts`} defaultMessage="total"/>}
                icon={'/media/common/Money_navy.svg'}
                customClassnames={'w-250px ml-2'}
              >
                <StyledFieldInput
                  dir={'ltr'}
                  fieldId={'totalPaid'}
                  label={<FormattedMessage id={`${prefix}-label-total-receipts`} defaultMessage="total"/>}
                  customContainerStyles={'w-200px'}
                  customInputStyles={'text-left bg-secondary'}
                  value={divideThousands(getTotalPaid(values)) || '0.00'}
                  disabled={true}
                />
              </Unit>
            </div>
          </>
        );
    }
  };

  return (
    <>
      <ButtonAction
        variant={buttonVariant}
        iconButtonType={iconButtonType}
        useIcon={icon}
        label={buttonVariant === 'icon' ? buttonIcon : title}
        onClick={() => handleShowModal(documentId)}
        customClassnames={'mx-2'}
      />
      <ModalContainer show={show} onHide={handleCloseModal} size={'xxxl'}>
        <ModalHeader
          type={'complex'}
          icon={'/media/common/Edit_primary.svg'}
          header={Boolean(linkedDocumentsIds) && documentType === T.RECEIPT ?
            <div className="d-flex justify-content-center align-items-center">
              <h2 className="text-center font-weight-bolder mr-4 mb-0">
                <FormattedMessage id={`${prefix}-label-related-to-invoice`} defaultMessage="Related to invoice"/>
              </h2>
              <StyledFieldInput
                fieldId={'linkedDocumentIds'}
                value={publicDocId}
                customContainerStyles="w-100px"
                title={values.linkedDocumentIds}
                customInputStyles={'text-nowrap text-truncate bg-secondary'}
                disabled={true}
              />
            </div>
            : <span>{title}</span>
          }
        />
        <ModalContent isScrollable={true}>
          {!(loadingCopiedDocumentData && withCopiedData)
            ?
            <div>
              <div className={'d-flex justify-content-between mb-4'}>
                <Unit
                  text={<FormattedMessage id={`${prefix}-date`} defaultMessage="Date"/>}
                  icon={'/media/common/Date_green.svg'}
                  customClassnames={'w-50 mr-2'}
                >
                  <div className={'d-flex justify-content-between'}>
                    <StyledFieldEmailChips
                      fieldId={'emails'}
                      label={
                        <div className="position-relative">
                          <FormattedMessage id={`${prefix}-label-emails`} defaultMessage="Emails"/>
                          <span className={'text-danger'}>*</span>
                        </div>
                      }
                      customerEmails={initialEmailsValue}
                      setFieldValue={setFieldValue}
                      customContainerStyles={'w-300px'}
                    />
                    <StyledFieldDatepicker
                      value={values.createdDate}
                      onChange={(date) => setFieldValue('createdDate', new Date(date))}
                      label={<FormattedMessage id={`${prefix}-date`} defaultMessage="Date"/>}
                      minDate={moment().subtract(1, 'months').format('YYYY-MM-DD')}
                      maxDate={moment().add(1, 'years').format('YYYY-MM-DD')}
                      customContainerStyles={'mx-4 w-200px'}
                    />
                    <StyledFieldTextareaAutosize
                      fieldId={'description'}
                      label={
                        <div className={'justify-content-start'}>
                          <FormattedMessage id={`${prefix}-label-description`} defaultMessage="Description"/>
                          <ButtonTextSuggestions
                            fieldName={'description'}
                            currentFieldValue={values.description}
                            setFieldValue={setFieldValue}
                            textSuggestionsArray={getSuggestions(description)}
                            disabled={getSuggestions(description).length === 0}
                          />
                        </div>
                      }
                      customContainerStyles={'w-50'}
                      onChange={handleChange}
                      value={values.description}
                    />
                  </div>
                </Unit>
              </div>
              {renderGridsAmounts(documentType)}
              <Unit
                text={<FormattedMessage id={`${prefix}-label-desc-header`} defaultMessage="Additional info"/>}
                icon={'/media/common/Description_orange.svg'}
                customClassnames={'mt-4'}
              >
                <StyledFieldTextareaAutosize
                  fieldId={'emailContent'}
                  label={<FormattedMessage id={`${prefix}-label-emailContent`} defaultMessage="emailContent"/>}
                  onChange={handleChange}
                />
                <div className={'d-flex justify-content-between mt-4'}>
                  <StyledFieldTextareaAutosize
                    fieldId={'remarks'}
                    label={
                      <div className={'justify-content-start'}>
                        <FormattedMessage id={`${prefix}-label-remarks`} defaultMessage="remarks"/>
                        <ButtonTextSuggestions
                          fieldName={'remarks'}
                          currentFieldValue={values.remarks}
                          setFieldValue={setFieldValue}
                          textSuggestionsArray={getSuggestions(remarks)}
                          disabled={getSuggestions(remarks).length === 0}
                        />
                      </div>
                    }
                    value={values.remarks}
                    customContainerStyles={'mr-4'}
                    onChange={handleChange}
                    minRows={20}
                    maxRows={30}
                  />
                  <StyledFieldTextareaAutosize
                    fieldId={'footer'}
                    label={
                      <div className={'justify-content-start'}>
                        <FormattedMessage id={`${prefix}-label-footer`} defaultMessage="footer"/>
                        <ButtonTextSuggestions
                          fieldName={'footer'}
                          currentFieldValue={values.footer}
                          setFieldValue={setFieldValue}
                          textSuggestionsArray={getSuggestions(footer)}
                          disabled={getSuggestions(footer).length === 0}
                        />
                      </div>
                    }
                    value={values.footer}
                    onChange={handleChange}
                    minRows={20}
                    maxRows={30}
                  />
                </div>
              </Unit>
            </div>
            : <div className={'w-100 d-flex justify-content-center'}><Spinner/></div>
          }
        </ModalContent>
        <ModalButtonsContainer customClassnames={largeButtonsPadding}>
          <ButtonAction
            variant={'cancel'}
            label={<FormattedMessage id="button-close" defaultMessage="Close"/>}
            onClick={() => {
              setCreateFromCopiedDataMark(false);
              handleCloseModal();
            }}
            customClassnames={buttonCancel}
          />
          <PopupPreviewDocument
            buttonType={'success'}
            documentPath={`data:application/pdf;base64,${previewDocumentLink}`}
            handlePreviewDocument={() => {
              handlePreviewDocument(
                documentType,
                linkedDocumentsIds,
                createdFromDocType
              );
            }}
            documentType={documentType}
            allowHandlePreviewDocument={true}
            disableButton={disabledButtonCondition}
          />
          {withCopiedData && documentType === 400 &&
          (copiedDocumentData.amount < getTotalPaid(values))
            ?
            <>
              <ButtonAction
                variant={'warning'}
                label={<FormattedMessage id={`${prefix}-button-label-create-document`}
                                         defaultMessage="Create document"/>}
                onClick={() => setShowApprove(true)}
                disabled={disabledButtonCondition}
              />
              <ModalContainer show={showApprove} onHide={() => setShowApprove(false)}>
                <ModalHeader header={
                  <FormattedMessage
                    id="customer-details-popup-approve-decline-create-receipt"
                    defaultMessage="The amount typed is higher than the invoice total!"
                  />
                }/>
                <ModalButtonsContainer customClassnames={largeButtonsPadding}>
                  <ButtonAction
                    variant={'cancel'}
                    label={<FormattedMessage id="button-close" defaultMessage="Close"/>}
                    onClick={() => setShowApprove(false)}
                    customClassnames={buttonCancel}
                  />
                  <ButtonAction
                    useIcon={'/media/common/Check-in-circle_white.svg'}
                    label={<FormattedMessage id="button-submit" defaultMessage="Submit"/>}
                    onClick={() => {
                      handleCreateDocument(
                        documentType,
                        linkedDocumentsIds,
                        createdFromDocType
                      );
                      setCreateFromCopiedDataMark(false);
                      setShowApprove(false);
                      handleCloseModal();
                    }}
                    disabled={disabledButtonCondition}
                    customClassnames={buttonSubmit}
                  />
                </ModalButtonsContainer>
              </ModalContainer>
            </>
            :
            <ButtonAction
              label={<FormattedMessage id={`${prefix}-button-label-create-document`} defaultMessage="Create document"/>}
              onClick={() => {
                handleCreateDocument(
                  documentType,
                  linkedDocumentsIds,
                  createdFromDocType
                );
                setCreateFromCopiedDataMark(false);
                handleCloseModal();
              }}
              useIcon={'/media/common/Check-in-circle_white.svg'}
              disabled={disabledButtonCondition}
              customClassnames={buttonSubmit}
            />
          }
        </ModalButtonsContainer>
      </ModalContainer>
    </>
  );
};

export default PopupCreateDocument;